import { useMutation, useQuery, useQueryClient } from 'react-query'
import { apiService } from '@/services/apiService'

/**
 * Direct-message hooks. Live updates are pure polling — the open thread
 * refreshes every 10s, inbox every 30s, unread badge every 60s, all
 * paused when the tab is in the background. Earlier values were too hot
 * (4/15/30s) and tripped the backend rate limiter within seconds of
 * opening a thread; current cadence keeps comfortably under the per-IP
 * ceiling while still feeling near-live.
 */

export const messagesKeys = {
  inbox: ['messages', 'inbox'] as const,
  thread: (id: string) => ['messages', 'thread', id] as const,
  unread: ['messages', 'unread'] as const,
}

export function useConversations() {
  return useQuery({
    queryKey: messagesKeys.inbox,
    queryFn: () => apiService.getConversations(),
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

export function useConversation(conversationId: string | null) {
  return useQuery({
    queryKey: conversationId ? messagesKeys.thread(conversationId) : ['messages', 'thread', 'none'],
    queryFn: () => apiService.getConversation(conversationId as string),
    enabled: !!conversationId,
    staleTime: 5 * 1000,
    refetchInterval: 10 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

export function useUnreadMessageCount() {
  return useQuery({
    queryKey: messagesKeys.unread,
    queryFn: () => apiService.getUnreadMessageCount(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

export function useSendMessage(conversationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: string) => apiService.sendMessage(conversationId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagesKeys.thread(conversationId) })
      qc.invalidateQueries({ queryKey: messagesKeys.inbox })
      qc.invalidateQueries({ queryKey: messagesKeys.unread })
    },
  })
}

export function useDeleteMessage(conversationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (messageId: string) => apiService.deleteMessage(conversationId, messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagesKeys.thread(conversationId) })
      qc.invalidateQueries({ queryKey: messagesKeys.inbox })
    },
  })
}

export function useOpenConversationWith() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (recipientId: string) => apiService.openConversationWith(recipientId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagesKeys.inbox })
    },
  })
}
